import { useEffect, useRef, useState, useCallback } from 'react';
import { endOfDay } from 'date-fns';

import { Messages } from 'primereact/messages';
import { ProgressSpinner } from 'primereact/progressspinner';

import ReportingService from '../services/reportingService';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { Tenant } from '../models/tenant';
import { AppPage } from '../components/general';
import { LineChartWidget, PieChartWidget } from '../components/widget';
import { ChartSeries, PieSeries } from '../models/chart/chart';
import { CreditSummary, FeesSummary, VoxSummary, AuwSummary, VendorBreakdownItem, VoxVendorBreakdownItem, AuwVendorBreakdownItem } from '../models/reporting/reporting';
import CalendarRange, { DefaultRangeSelectType } from '../components/common/CalendarRange';
import SummaryCard from '../components/dashboard/SummaryCard';
import VendorTable from '../components/dashboard/VendorTable';
import VoxVendorTable from '../components/dashboard/VoxVendorTable';
import AuwVendorTable from '../components/dashboard/AuwVendorTable';
import DashboardDetailDialog, { DetailType } from '../components/dashboard/DashboardDetailDialog';

export type ProductType = 'credit' | 'fees' | 'vox' | 'auw' | 'price';

interface ProductConfig {
  title: string;
  icon: string;
  color: string;
  detailType: DetailType;
  fetchKey: 'credit' | 'fees' | 'vox' | 'auw';
}

const productConfigs: Record<ProductType, ProductConfig> = {
  credit: { title: 'Credit', icon: 'pi-credit-card', color: '#0a1f3d', detailType: 'credit', fetchKey: 'credit' },
  fees: { title: 'Fees', icon: 'pi-dollar', color: '#2571ab', detailType: 'fees', fetchKey: 'fees' },
  vox: { title: 'VoX', icon: 'pi-bolt', color: '#76b729', detailType: 'vox', fetchKey: 'vox' },
  auw: { title: 'Automated Underwriting', icon: 'pi-shield', color: '#0a1f3d', detailType: 'auw', fetchKey: 'auw' },
  price: { title: 'Price', icon: 'pi-tag', color: '#6366f1', detailType: 'credit', fetchKey: 'credit' },
};

interface ProductDashboardProps {
  product: ProductType;
}

const ProductDashboard = ({ product }: ProductDashboardProps): JSX.Element => {
  const config = productConfigs[product];
  const errors = useRef<Messages>(null);
  const { tenant: authTenant } = useAuth();
  const tenant = authTenant as unknown as Tenant | undefined;

  const isProductDisabled = (): boolean => {
    if (!tenant?.enabledProducts) return false;
    const productMap: Record<ProductType, boolean | undefined> = {
      credit: tenant.enabledProducts.credit,
      fees: tenant.enabledProducts.fees,
      vox: tenant.enabledProducts.vox,
      auw: tenant.enabledProducts.automatedUnderwrite,
      price: tenant.enabledProducts.pricing,
    };
    return productMap[product] === false;
  };

  const [loading, setLoading] = useState(true);
  const [credit, setCredit] = useState<CreditSummary | null>(null);
  const [fees, setFees] = useState<FeesSummary | null>(null);
  const [vox, setVox] = useState<VoxSummary | null>(null);
  const [auw, setAuw] = useState<AuwSummary | null>(null);

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [dateRange, setDateRange] = useState<Date[] | undefined>([start, end]);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailVendor, setDetailVendor] = useState<string | undefined>();
  const [detailStatus, setDetailStatus] = useState<string | undefined>();

  const reportingService = ReportingService.getInstance();

  const showError = (error: string) => {
    errors.current?.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const fetchData = useCallback(
    (startDate: Date, endDate: Date) => {
      setLoading(true);
      const key = config.fetchKey;

      if (key === 'fees') {
        reportingService
          .getFeesSummary(startDate, endDate)
          .then((data) => setFees(data))
          .catch((err: Error) => showError(err.message))
          .finally(() => setLoading(false));
      } else if (key === 'credit') {
        reportingService
          .getCreditSummary(startDate, endDate)
          .then((data) => setCredit(data))
          .catch((err: Error) => showError(err.message))
          .finally(() => setLoading(false));
      } else if (key === 'vox') {
        reportingService
          .getVoxSummary(startDate, endDate)
          .then((data) => setVox(data))
          .catch((err: Error) => showError(err.message))
          .finally(() => setLoading(false));
      } else if (key === 'auw') {
        reportingService
          .getAuwSummary(startDate, endDate)
          .then((data) => setAuw(data))
          .catch((err: Error) => showError(err.message))
          .finally(() => setLoading(false));
      }
    },
    [reportingService, config.fetchKey]
  );

  useEffect(() => {
    setCredit(null);
    setFees(null);
    setVox(null);
    setAuw(null);
    setDateRange([start, end]);
    fetchData(start, end);
  }, [product]);

  const openDetail = (vendor?: string, status?: string) => {
    setDetailVendor(vendor);
    setDetailStatus(status);
    setDetailVisible(true);
  };

  const buildDailyChart = (
    dailyCounts: { date: Date; count: number; successCount: number; failedCount: number }[] | undefined
  ): ChartSeries[] | undefined => {
    if (!dailyCounts || dailyCounts.length === 0) return undefined;
    return [
      { name: 'Success', data: dailyCounts.map((d) => ({ x: new Date(d.date).getTime(), y: d.successCount })) },
      { name: 'Failed', data: dailyCounts.map((d) => ({ x: new Date(d.date).getTime(), y: d.failedCount })) },
    ];
  };

  const buildVolumeChart = (
    dailyCounts: { date: Date; count: number; successCount: number; failedCount: number }[] | undefined
  ): ChartSeries[] | undefined => {
    if (!dailyCounts || dailyCounts.length === 0) return undefined;
    return [
      { name: 'Total', data: dailyCounts.map((d) => ({ x: new Date(d.date).getTime(), y: d.count })) },
    ];
  };

  const buildStatusPie = (
    statusBreakdown: { status: string; count: number }[] | undefined
  ): PieSeries | undefined => {
    if (!statusBreakdown || statusBreakdown.length === 0) return undefined;
    return {
      labels: statusBreakdown.map((s) => s.status),
      data: statusBreakdown.map((s) => s.count),
    };
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  };

  const renderSpinner = () => (
    <div className="flex justify-center items-center py-20">
      <ProgressSpinner style={{ width: '60px', height: '60px' }} />
    </div>
  );

  // Normalize data for rendering
  const isOrderBased = config.fetchKey === 'vox' || config.fetchKey === 'auw';
  const data =
    config.fetchKey === 'fees' ? fees :
    config.fetchKey === 'vox' ? vox :
    config.fetchKey === 'auw' ? auw :
    credit;
  const totalRequests = config.fetchKey === 'vox'
    ? (vox?.totalOrders ?? 0)
    : config.fetchKey === 'auw'
      ? (auw?.totalOrders ?? 0)
      : ((data as any)?.totalRequests ?? 0);
  const successCount = data?.successCount ?? 0;
  const failedCount = data?.failedCount ?? 0;
  const successRate = data?.successRate ?? 0;
  const dailyAverage = data?.dailyAverage ?? 0;
  const dailyCounts = data?.dailyCounts;
  const statusBreakdown = data?.statusBreakdown;
  const avgDurationMs = !isOrderBased ? ((data as any)?.avgDurationMs ?? 0) : 0;
  const vendorBreakdown: VendorBreakdownItem[] = !isOrderBased ? ((data as any)?.vendorBreakdown ?? []) : [];
  const voxVendorBreakdown: VoxVendorBreakdownItem[] = config.fetchKey === 'vox' ? (vox?.vendorBreakdown ?? []) : [];
  const auwVendorBreakdown: AuwVendorBreakdownItem[] = config.fetchKey === 'auw' ? (auw?.vendorBreakdown ?? []) : [];
  const pendingCount = config.fetchKey === 'vox'
    ? (vox?.pendingCount ?? 0)
    : config.fetchKey === 'auw'
      ? (auw?.pendingCount ?? 0)
      : 0;

  // For Price, show a coming soon state if no dedicated API
  const isPlaceholder = product === 'price';

  // For Credit, VoX, and Fees, show disabled state when product is not enabled
  const isDisabled = !isPlaceholder && isProductDisabled();

  return (
    <AppPage
      title={config.title}
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: config.title },
      ]}
      headerEnd={
        !isPlaceholder ? (
          <CalendarRange
            rangeSelectOptions={[
              DefaultRangeSelectType.PastWeek,
              DefaultRangeSelectType.Past2Weeks,
              DefaultRangeSelectType.Past30Days,
              DefaultRangeSelectType.CurrentMonth,
            ]}
            value={dateRange}
            onValueChange={(value) => {
              if (!!value && value.length === 2) {
                fetchData(value[0], value[1]);
              }
              setDateRange(value);
            }}
          />
        ) : undefined
      }
    >
      <Messages ref={errors} />

      {isDisabled ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl mb-4"
            style={{ background: config.color }}
          >
            <i className={`pi ${config.icon}`} />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{config.title}</h2>
          <p className="text-gray-400 max-w-md">
            {config.title} is not currently enabled for your organization. Please reach out to your Mezzo representative to get started.
          </p>
        </div>
      ) : isPlaceholder ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl mb-4"
            style={{ background: config.color }}
          >
            <i className={`pi ${config.icon}`} />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{config.title} Dashboard</h2>
          <p className="text-gray-400 max-w-md">
            Product-specific analytics for {config.title} are coming soon. Check the Overview Dashboard for aggregated metrics.
          </p>
        </div>
      ) : loading ? (
        renderSpinner()
      ) : (
        <div className="flex flex-col gap-6">

          {/* ═══════════ KPIs ═══════════ */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', width: '100%' }}>
            <SummaryCard
              title={isOrderBased ? 'Total Orders' : 'Total Requests'}
              value={totalRequests.toLocaleString()}
              icon="pi pi-chart-bar"
              iconBgStyle={config.color}
              onClick={() => openDetail()}
            />
            <SummaryCard
              title="Successful"
              value={successCount.toLocaleString()}
              icon="pi pi-check-circle"
              iconColor="bg-[#76b729]"
              onClick={() => openDetail(undefined, 'Success')}
            />
            <SummaryCard
              title="Failed"
              value={failedCount.toLocaleString()}
              icon="pi pi-times-circle"
              iconColor="bg-[#e4677e]"
              onClick={() => openDetail(undefined, 'Failed')}
            />
            <SummaryCard
              title="Success Rate"
              value={`${successRate}%`}
              icon="pi pi-percentage"
              iconColor="bg-[#2571ab]"
            />
            {isOrderBased ? (
              <SummaryCard
                title="Pending"
                value={pendingCount.toLocaleString()}
                icon="pi pi-hourglass"
                iconColor="bg-[#f5a623]"
                onClick={() => openDetail(undefined, 'Pending')}
              />
            ) : config.fetchKey === 'fees' ? (
              <div className="rounded-xl px-5 py-4 bg-white border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[0.7rem] font-semibold text-gray-400 uppercase tracking-wide">Call Times</div>
                  <div className="rounded-xl flex items-center justify-center text-white text-lg flex-shrink-0 bg-[#0a1f3d]" style={{ width: '44px', height: '44px' }}>
                    <i className="pi pi-clock" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="text-gray-400">Rules</div>
                  <div className="font-semibold text-gray-800 text-right">{formatDuration(fees?.avgRuleExecutionMs ?? 0)}</div>
                  <div className="text-gray-400">3rd Party</div>
                  <div className="font-semibold text-gray-800 text-right">{formatDuration(fees?.avgServiceProviderMs ?? 0)}</div>
                  <div className="text-gray-400">Total</div>
                  <div className="font-semibold text-gray-800 text-right">{formatDuration(fees?.avgTotalDurationMs ?? avgDurationMs)}</div>
                </div>
              </div>
            ) : (
              <SummaryCard
                title="Avg Duration"
                value={formatDuration(avgDurationMs)}
                subtitle={`${dailyAverage} / day`}
                icon="pi pi-clock"
                iconColor="bg-[#0a1f3d]"
              />
            )}
          </div>

          {/* ═══════════ Full-width Volume Chart ═══════════ */}
          <div style={{ width: '100%', minWidth: 0 }}>
            <LineChartWidget
              title={`Daily ${config.title} Volume`}
              chartSeries={buildVolumeChart(dailyCounts)}
              chartHeight="350px"
            />
          </div>

          {/* ═══════════ Vendor Table ═══════════ */}
          {config.fetchKey === 'vox' ? (
            <VoxVendorTable
              title="VoX Vendors"
              vendors={voxVendorBreakdown}
              onVendorClick={(vendorId) => openDetail(String(vendorId))}
            />
          ) : config.fetchKey === 'auw' ? (
            <AuwVendorTable
              title="AUW Vendors"
              vendors={auwVendorBreakdown}
              onVendorClick={(vendorId) => openDetail(String(vendorId))}
            />
          ) : (
            <VendorTable
              title={`${config.title} Vendors`}
              vendors={vendorBreakdown}
              onVendorClick={(vendor) => openDetail(vendor)}
            />
          )}

          {/* ═══════════ Success/Fail + Status Pie ═══════════ */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem', width: '100%' }}>
            <div style={{ minWidth: 0 }}>
              <LineChartWidget
                title="Success vs Failed"
                chartSeries={buildDailyChart(dailyCounts)}
                chartHeight="320px"
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <PieChartWidget
                title="Status Breakdown"
                pieSeries={buildStatusPie(statusBreakdown)}
                chartHeight="320px"
              />
            </div>
          </div>
        </div>
      )}

      <DashboardDetailDialog
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        type={config.detailType}
        startDate={dateRange?.[0] ?? start}
        endDate={dateRange?.[1] ?? end}
        vendor={detailVendor}
        status={detailStatus}
      />
    </AppPage>
  );
};

export default ProductDashboard;
